import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scissors, CheckCircle2 } from 'lucide-react';
type Service = {
  title: string;
  price: string;
  image: string;
};
type Notification = {
  id: number;
  name: string;
  city: string;
  service: Service;
  timeAgo: string;
};
// Local-area cities (Gwinnett / Atlanta metro feels authentic for a Lawrenceville barbershop)
const CITIES = ['Lawrenceville, GA', 'Duluth, GA', 'Suwanee, GA', 'Snellville, GA', 'Norcross, GA', 'Buford, GA', 'Sugar Hill, GA', 'Grayson, GA', 'Loganville, GA', 'Dacula, GA', 'Atlanta, GA', 'Decatur, GA', 'Stone Mountain, GA', 'Tucker, GA', 'Lilburn, GA', 'Johns Creek, GA', 'Alpharetta, GA', 'Roswell, GA'];
const NAMES = ['Michael R.', 'Carlos M.', 'Jordan B.', 'Anthony L.', 'David K.', 'Marcus J.', 'Tyler S.', 'Brandon W.', 'Jose A.', 'Kevin D.', 'Andre T.', 'Christian P.', 'Ethan H.', 'Diego R.', 'Aaron C.', 'Justin V.', 'Nathan G.', 'Cameron F.', 'Isaiah M.', 'Daniel O.', 'Luis F.', 'Trevor B.', 'Malik S.', 'Adrian N.', 'Sebastian R.', 'Jamal W.', 'Noah K.', 'Elijah D.', 'Mason T.', 'Xavier H.'];
// Fallback service catalog — reflects what Cristian Barber offers
const FALLBACK_SERVICES: Service[] = [{
  title: 'Signature Cut & Fade',
  price: '$35',
  image: 'https://cdn.magicpatterns.com/uploads/8zwLEs5Vrkvkxygt9QiiMX/b2.png'
}, {
  title: 'Haircut + Beard Trim',
  price: '$45',
  image: 'https://cdn.magicpatterns.com/uploads/oASX3fhkTfB9qYFmJdZoGj/b3.png'
}, {
  title: 'Custom Hair Design',
  price: '$50',
  image: 'https://cdn.magicpatterns.com/uploads/cHZFsxDbXN1K43xHTacbrj/b1.png'
}, {
  title: 'Waves & Line Up',
  price: '$30',
  image: 'https://cdn.magicpatterns.com/uploads/orKBsne1YtZun27E8kJ7BK/b10.png'
}, {
  title: 'Premium Grooming Experience',
  price: '$60',
  image: 'https://cdn.magicpatterns.com/uploads/chJYSP3JGRsQpTHCtV397P/b12.png'
}];
// Try to scan the DOM for products with prices; fall back to curated services
const scanCatalog = (): Service[] => {
  try {
    const priceRegex = /\$\s?(\d+(?:\.\d{1,2})?)/;
    const candidates: Service[] = [];
    // 1. JSON-LD
    const ldNodes = document.querySelectorAll('script[type="application/ld+json"]');
    ldNodes.forEach(node => {
      try {
        const data = JSON.parse(node.textContent || '{}');
        const arr = Array.isArray(data) ? data : [data];
        arr.forEach((item: any) => {
          if (item['@type'] === 'Product' && item.offers) {
            const offer = Array.isArray(item.offers) ? item.offers[0] : item.offers;
            if (offer?.price) {
              candidates.push({
                title: item.name || 'Service',
                price: `$${offer.price}`,
                image: item.image || FALLBACK_SERVICES[0].image
              });
            }
          }
        });
      } catch {}
    });
    // 2. Scan for price-like text near images
    if (candidates.length === 0) {
      const all = document.querySelectorAll('[data-price], .price, [class*="price"]');
      all.forEach(el => {
        const text = (el as HTMLElement).innerText || '';
        const m = text.match(priceRegex);
        if (m) {
          const parent = el.closest('article, section, div');
          const img = parent?.querySelector('img') as HTMLImageElement | null;
          const titleEl = parent?.querySelector('h1, h2, h3, h4') as HTMLElement | null;
          candidates.push({
            title: titleEl?.innerText?.trim() || 'Service',
            price: `$${m[1]}`,
            image: img?.src || FALLBACK_SERVICES[0].image
          });
        }
      });
    }
    return candidates.length > 0 ? candidates : FALLBACK_SERVICES;
  } catch {
    return FALLBACK_SERVICES;
  }
};
const TIME_AGO_OPTIONS = ['just now', '1 min ago', '2 min ago', '3 min ago', '5 min ago', '7 min ago', '12 min ago'];
export const BookingToast: React.FC = () => {
  const [current, setCurrent] = useState<Notification | null>(null);
  const [catalog] = useState<Service[]>(() => scanCatalog());
  const recentNamesRef = useRef<string[]>([]);
  const counterRef = useRef(0);
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const pickName = (): string => {
    const recent = recentNamesRef.current;
    const available = NAMES.filter(n => !recent.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = pool[Math.floor(Math.random() * pool.length)];
    recentNamesRef.current = [...recent, name].slice(-10);
    return name;
  };
  const createNotification = (): Notification => {
    counterRef.current += 1;
    return {
      id: counterRef.current,
      name: pickName(),
      city: CITIES[Math.floor(Math.random() * CITIES.length)],
      service: catalog[Math.floor(Math.random() * catalog.length)],
      timeAgo: TIME_AGO_OPTIONS[Math.floor(Math.random() * TIME_AGO_OPTIONS.length)]
    };
  };
  useEffect(() => {
    let visibleTimer: ReturnType<typeof setTimeout>;
    let nextTimer: ReturnType<typeof setTimeout>;
    let cancelled = false;
    const showNext = () => {
      if (cancelled) return;
      setCurrent(createNotification());
      visibleTimer = setTimeout(() => {
        if (cancelled) return;
        setCurrent(null);
        const wait = 8000 + Math.random() * 7000;
        nextTimer = setTimeout(showNext, wait);
      }, 4000);
    };
    const initialTimer = setTimeout(showNext, 3000);
    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      clearTimeout(visibleTimer);
      clearTimeout(nextTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const motionProps = reducedMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 30,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    transition: {
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
    }
  };
  return <div aria-live="polite" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] w-[calc(100%-2rem)] max-w-md pointer-events-none" data-id="element-423">
      <AnimatePresence mode="wait" data-id="element-424">
        {current && <motion.div key={current.id} {...motionProps} className="pointer-events-auto bg-darker/95 backdrop-blur-md border border-gold/30 shadow-2xl shadow-black/50 rounded-sm overflow-hidden" data-id="element-425">
            <div className="flex items-stretch gap-3 p-3" data-id="element-426">
              {/* Body */}
              <div className="flex-1 min-w-0 flex flex-col justify-center" data-id="element-427">
                <div className="flex items-center gap-1.5 text-gold mb-0.5" data-id="element-428">
                  <CheckCircle2 size={12} data-id="element-429" />
                  <span className="text-[10px] tracking-[0.2em] uppercase font-medium" data-id="element-430">
                    New Booking
                  </span>
                </div>
                <p className="text-sm text-cream leading-snug truncate" data-id="element-431">
                  <span className="font-medium" data-id="element-432">{current.name}</span>
                  <span className="text-cream/60" data-id="element-433"> in </span>
                  <span className="font-medium" data-id="element-434">{current.city}</span>
                </p>
                <p className="text-xs text-cream/60 leading-snug truncate" data-id="element-435">
                  Booked{' '}
                  <span className="text-gold/90" data-id="element-436">{current.service.title}</span>
                  <span className="text-cream/40" data-id="element-437">
                    {' '}
                    · {current.service.price}
                  </span>
                </p>
              </div>

              {/* Time + scissors */}
              <div className="flex flex-col items-end justify-between shrink-0 pl-1" data-id="element-438">
                <Scissors size={12} className="text-gold/60" data-id="element-439" />
                <span className="text-[9px] tracking-widest uppercase text-cream/40 whitespace-nowrap" data-id="element-440">
                  {current.timeAgo}
                </span>
              </div>
            </div>

            {/* Progress bar */}
            {!reducedMotion && <motion.div key={`bar-${current.id}`} initial={{
          scaleX: 1
        }} animate={{
          scaleX: 0
        }} transition={{
          duration: 4,
          ease: 'linear'
        }} style={{
          transformOrigin: 'left'
        }} className="h-[2px] bg-gold/70" data-id="element-441" />}
          </motion.div>}
      </AnimatePresence>
    </div>;
};